import { getShortId, type Product } from '@/domain/Product.ts';

export type Subscription = {
  id: string;
  type: 'COFFEE' | 'MACHINE';
  productIds: string[];
  expires: Date;
  parameters: {
    headline: string;
    monthsDuration: number;
    promotionalPrice: number;
    firstFee: number;
    periodicFee: number;
    terminationFee: number;
  };
  title: string;
  description: string;
  offerDescription?: string;
  mediaUrl?: string;
};

export type SubscriptionsByDuration = { [duration: string]: Subscription[] };

export type AboutSubscriptions = {
  description: string;
  features: SubscriptionFeature[];
  faqs: SubscriptionFAQ[];
  moreInfoLink?: {
    label: string;
    url: string;
  };
  termsAndConditions: string;
};

export type SubscriptionFeature = {
  icon: string;
  description: string;
};

export type SubscriptionFAQ = {
  question: string;
  answer: string;
};

export function getByDurationForProduct(
  subscriptions: Subscription[],
  product: Pick<Product, 'id' | 'isSubscriptionOnly'>
): Record<number, Subscription[]> {
  const now = new Date();
  const productSubscriptions = subscriptions
    .filter((sub) => sub.productIds.includes(getShortId(product.id)))
    .filter((sub) => sub.expires > now)
    .sort(
      (subA, subB) => subA.parameters.periodicFee - subB.parameters.periodicFee
    );

  return Object.groupBy(
    productSubscriptions,
    (sub) => sub.parameters.monthsDuration
  ) as Record<number, Subscription[]>;
}

export function flattenDurationsIntoSingleDuration(
  subscriptionsByDuration: Record<number, Subscription[]>
): Record<number, Subscription[]> {
  const productSubscriptions = Object.values(subscriptionsByDuration).flat();

  return {
    Infinity: productSubscriptions.map((sub) => ({
      ...sub,
      monthsDuration: 0
    }))
  };
}

export function getLowestPricedSubscription(
  subscriptionsByDuration: Record<number, Subscription[]>
): Subscription {
  const subscriptions = Object.values(subscriptionsByDuration).flat();
  return subscriptions.reduce(
    (currentLowestSubscription: Subscription, sub: Subscription) => {
      if (
        !currentLowestSubscription ||
        currentLowestSubscription.parameters.promotionalPrice >
          sub.parameters.promotionalPrice
      ) {
        return sub;
      }
      return currentLowestSubscription;
    },
    subscriptions[0]
  );
}

export function getListOfSubscriptionsDurations(
  subscriptionsByDuration: SubscriptionsByDuration
): number[] {
  const subscriptionsDurations = Object.values(subscriptionsByDuration).flat();
  return subscriptionsDurations.map(
    (subscription) => subscription?.parameters?.monthsDuration
  );
}

export function getSelectedSubscription(
  subscriptionsByDuration: SubscriptionsByDuration,
  selectedSubscriptionId: string
): Subscription | undefined {
  for (const duration in subscriptionsByDuration) {
    if (
      Object.prototype.hasOwnProperty.call(subscriptionsByDuration, duration)
    ) {
      const subscriptionsArray = subscriptionsByDuration[duration];
      const selectedSubscription = subscriptionsArray.find(
        (subscription) => subscription.id === selectedSubscriptionId
      );

      if (selectedSubscription) {
        return selectedSubscription;
      }
    }
  }

  return undefined;
}

export function findDurationFromSubscriptionId(
  subscriptionsByDuration: { [duration: string]: Subscription[] },
  subscriptionId: string
): number | false {
  return Object.entries(subscriptionsByDuration).reduce<number | false>(
    (duration: number | false, entry: [string, Subscription[]]) => {
      if (duration) return duration;

      const [newDuration, subscriptions] = entry;

      if (
        subscriptions.find(
          (subscription: Subscription) => subscription.id === subscriptionId
        )
      ) {
        return parseInt(newDuration, 10);
      }
      return false;
    },
    false
  );
}
